import { UserAvatar } from '@/components/ui/user-avatar';
import { EditableTitleSelect } from '@/components/ui/editable-title-select';
import { cn } from '@/lib/utils';
import React, { useState } from 'react';
import { TokenizedEmailAutocomplete } from '@/components/email/TokenizedEmailAutocomplete';

interface UserInfoProps {
  initials?: string;
  name: string;
  subtitle?: string;
  userId?: string;
  currentTitle?: string;
  editableTitle?: boolean;
  onTitleUpdate?: (newTitle: string) => void;
  avatarClassName?: string;
  /** Email shown below the name. */
  email?: string;
  /** Enable inline editing of the author's email local-part (before @tebra.com). */
  editableEmail?: boolean;
  /** Callback when email is updated and validated. */
  onEmailUpdate?: (newEmail: string) => void;
  className?: string;
}

export function UserInfo({
  initials,
  name,
  subtitle,
  userId,
  currentTitle,
  editableTitle = false,
  onTitleUpdate,
  avatarClassName,
  email,
  editableEmail = false,
  onEmailUpdate,
  className,
}: UserInfoProps) {
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailValues, setEmailValues] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [invalidEmailTokens, setInvalidEmailTokens] = useState<string[]>([]);
  const emailInputId = React.useId();

  const startEmailEdit = () => {
    if (!editableEmail) return;
    setEmailValues(email ? [email] : []);
    setEmailDraft('');
    setInvalidEmailTokens([]);
    setIsEditingEmail(true);
  };

  const handleEmailValuesChange = (emails: string[]) => {
    const nextEmails = emails.slice(-1);
    const nextEmail = nextEmails[0];
    setEmailValues(nextEmails);
    setInvalidEmailTokens([]);

    if (nextEmail) {
      if (nextEmail !== (email ?? '').toLowerCase()) {
        onEmailUpdate?.(nextEmail);
      }
      setEmailDraft('');
      setIsEditingEmail(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <UserAvatar initials={initials} className={cn('h-10 w-10', avatarClassName)} />
      <div className="flex flex-col justify-center">
        {editableEmail && isEditingEmail ? (
          <div className="min-w-[240px]">
            <TokenizedEmailAutocomplete
              id={emailInputId}
              ariaLabel="Author email"
              value={emailValues}
              draftValue={emailDraft}
              onChange={handleEmailValuesChange}
              onDraftChange={(draft) => {
                setEmailDraft(draft);
                setInvalidEmailTokens([]);
              }}
              onInvalidTokensChange={setInvalidEmailTokens}
              placeholder="author@tebra.com"
              maxEmails={1}
              autoFocus
              commitOnBlur
            />
            {invalidEmailTokens.length > 0 && (
              <span className="text-xs text-destructive">
                Invalid email format or domain: {invalidEmailTokens.join(', ')}
              </span>
            )}
          </div>
        ) : (
          <p
            className={cn(
              'text-lg leading-none cursor-pointer',
              name === 'Author' ? 'text-muted-foreground italic' : 'font-semibold'
            )}
            onClick={startEmailEdit}
          >
            {name}
            {name === 'Author' && editableEmail && (
              <span className="relative inline-block">
                <span className="absolute -top-3 -right-3 flex w-1.5 h-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                  <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-secondary"></span>
                </span>
              </span>
            )}
          </p>
        )}

        {!isEditingEmail &&
          (editableTitle && userId ? (
            <EditableTitleSelect
              userId={userId}
              currentTitle={currentTitle}
              onTitleUpdate={onTitleUpdate}
              className="mt-1"
              selectClassName="py-0 min-h-auto"
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {currentTitle || subtitle || (userId ? 'No title set' : '')}
            </p>
          ))}
      </div>
    </div>
  );
}
